/**
 * Clinical evaluation for the AAP 2022 calculator.
 *
 * Pure functions: thresholds come from `aapThresholds.ts`, every tunable
 * number/text comes from `Aap2022Settings` (hospital config), and the result is
 * a plain object the component renders. Keep all decision logic here so it can
 * be unit-tested without a DOM.
 */
import type { Aap2022Settings, FollowUpBand } from '../phototherapyConfig';
import { getAapThresholds, type AapThresholds } from './aapThresholds';

export type MeasurementType = 'TSB' | 'TcB';

export type AapStatus =
  /** Bilirubin at/above the exchange-transfusion threshold. */
  | 'exchange'
  /** Within `escalationDeltaMgDl` below the exchange threshold (escalation of care). */
  | 'escalation'
  /** At/above the phototherapy threshold. */
  | 'phototherapy'
  /** Below the phototherapy threshold by at most `subthresholdDeltaMgDl`. */
  | 'subthreshold'
  /** Comfortably below the phototherapy threshold. */
  | 'below';

export interface AapEvaluationInput {
  gestationalAgeWeeks: number;
  ageHours: number;
  bilirubinMgDl: number;
  hasNeurotoxicityRisk: boolean;
  measurement: MeasurementType;
}

export interface AapEvaluation {
  thresholds: AapThresholds;
  /**
   * TSB at which the escalation-of-care protocol starts: `escalationDeltaMgDl`
   * below the exchange threshold. Derived, not looked up — see `escalationLevel`.
   */
  escalationThreshold: number;
  /**
   * The phototherapy threshold on the WITH-RISK curve for the same gestational
   * age and age in hours. Equal to `thresholds.phototherapy` when the baby has a
   * risk factor. Only used for the blood-type/DAT rule below.
   */
  withRiskPhototherapyThreshold: number;
  status: AapStatus;
  /** phototherapy threshold − bilirubin (positive = below the threshold). */
  deltaToPhototherapy: number;
  /** exchange threshold − bilirubin (positive = below the threshold). */
  deltaToExchange: number;
  /** TSB level at which phototherapy may be stopped, per the configured delta. */
  stopPhototherapyLevel: number;
  /** True when a TcB reading must be confirmed with a serum TSB before acting. */
  tcbNeedsTsb: boolean;
  /**
   * True when the level is within `bloodTypeDatDeltaMgDl` of (or above) the
   * with-risk phototherapy threshold, which calls for a blood type + DAT sample
   * even in a baby with no risk factors. Always false when the delta is null.
   */
  bloodTypeDatNeeded: boolean;
  /** Discharge follow-up recommendation (only when age ≥ 12 h and bilirubin below threshold). */
  followUp: FollowUpBand | null;
  /** True when age < 12 h, where follow-up timing cannot be set from this measurement. */
  before12h: boolean;
  /**
   * Age ≥ configured hours and level within the configured delta below threshold.
   * Always false when `prolongedJaundiceFromHours` is null (rule not in use).
   */
  prolongedJaundice: boolean;
  /**
   * Why a hearing test (BERA) is recommended, or null when it is not:
   *  - `level`      — bilirubin at/above the configured level;
   *  - `escalation` — the level required the escalation-of-care protocol.
   * Both are off when `hearingTestTsbMgDl` is null (rule not in use).
   */
  hearingTestReason: 'level' | 'escalation' | null;
  /** Convenience flag: `hearingTestReason !== null`. */
  hearingTestRecommended: boolean;
}

const EPS = 1e-9;
const round1 = (n: number) => Math.round(n * 10) / 10;

export type ThresholdPair = Pick<AapThresholds, 'phototherapy' | 'exchange'>;

/**
 * The TSB at which the escalation-of-care protocol starts (procedure §10: "TSB
 * within 0-2 mg/dL below the EXCHANGE TRANSFUSION threshold"). The AAP publishes
 * two curves, not three, so this line is derived rather than stored, which is
 * what lets a hospital widen or narrow the band from the admin page.
 *
 * It is never allowed below the phototherapy threshold. The narrowest gap between
 * the two curves in the dataset is 4.9 mg/dL, so a hospital that configures a
 * wider delta would otherwise put "escalation of care" under a level that does
 * not even call for phototherapy, and the status ladder would stop being ordered.
 */
export function escalationLevel(t: ThresholdPair, escalationDeltaMgDl: number): number {
  return Math.max(t.exchange - escalationDeltaMgDl, t.phototherapy);
}

export function classifyStatus(
  bilirubin: number,
  t: ThresholdPair,
  subthresholdDeltaMgDl: number,
  escalationDeltaMgDl: number
): AapStatus {
  if (bilirubin + EPS >= t.exchange) return 'exchange';
  if (bilirubin + EPS >= escalationLevel(t, escalationDeltaMgDl)) return 'escalation';
  if (bilirubin + EPS >= t.phototherapy) return 'phototherapy';
  if (t.phototherapy - bilirubin <= subthresholdDeltaMgDl + EPS) return 'subthreshold';
  return 'below';
}

/**
 * Picks the follow-up band for a distance below the phototherapy threshold.
 * Distances are compared at one-decimal precision, matching how the guideline
 * tables are written (0.1–1.9, 2.0–3.4, ...). Returns null when no band matches
 * (for example a distance of 0.0, which is "at threshold", not a follow-up case).
 */
export function findFollowUpBand(delta: number, bands: FollowUpBand[]): FollowUpBand | null {
  const d = round1(delta);
  for (const band of bands) {
    const aboveMin = d + EPS >= band.minDelta;
    const belowMax = band.maxDelta === null || d <= band.maxDelta + EPS;
    if (aboveMin && belowMax) return band;
  }
  return null;
}

export function evaluateAap(input: AapEvaluationInput, settings: Aap2022Settings): AapEvaluation {
  const thresholds = getAapThresholds(
    input.gestationalAgeWeeks,
    input.ageHours,
    input.hasNeurotoxicityRisk
  );
  const b = input.bilirubinMgDl;
  const status = classifyStatus(
    b,
    thresholds,
    settings.subthresholdDeltaMgDl,
    settings.escalationDeltaMgDl
  );
  const deltaToPhototherapy = thresholds.phototherapy - b;
  const deltaToExchange = thresholds.exchange - b;
  const before12h = input.ageHours < 12;
  const belowThreshold = status === 'subthreshold' || status === 'below';

  const tcbNeedsTsb =
    input.measurement === 'TcB' &&
    (b + EPS >= settings.tcbToTsbAbsoluteMgDl ||
      deltaToPhototherapy <= settings.tcbToTsbDeltaMgDl + EPS);

  // Departmental procedure §2: the trigger is always the WITH-RISK curve, so a
  // baby with no risk factors of its own is screened for haemolysis well before
  // its own threshold. Reading the second curve is the only reason we look it up.
  const withRiskPhototherapyThreshold = input.hasNeurotoxicityRisk
    ? thresholds.phototherapy
    : getAapThresholds(input.gestationalAgeWeeks, input.ageHours, true).phototherapy;
  const bloodTypeDatNeeded =
    settings.bloodTypeDatDeltaMgDl !== null &&
    withRiskPhototherapyThreshold - b <= settings.bloodTypeDatDeltaMgDl + EPS;

  const followUp =
    belowThreshold && !before12h
      ? findFollowUpBand(deltaToPhototherapy, settings.followUpBands)
      : null;

  const prolongedJaundice =
    belowThreshold &&
    settings.prolongedJaundiceFromHours !== null &&
    input.ageHours >= settings.prolongedJaundiceFromHours &&
    deltaToPhototherapy <= settings.prolongedJaundiceDeltaMgDl + EPS;

  // Departmental procedure §12: refer for a hearing test when the level crossed
  // the configured value, and also whenever the escalation-of-care protocol was
  // triggered. A null level switches the whole recommendation off.
  const hearingTestReason: AapEvaluation['hearingTestReason'] =
    settings.hearingTestTsbMgDl === null
      ? null
      : b + EPS >= settings.hearingTestTsbMgDl
        ? 'level'
        : status === 'escalation' || status === 'exchange'
          ? 'escalation'
          : null;

  return {
    thresholds,
    escalationThreshold: escalationLevel(thresholds, settings.escalationDeltaMgDl),
    withRiskPhototherapyThreshold,
    status,
    deltaToPhototherapy,
    deltaToExchange,
    stopPhototherapyLevel: thresholds.phototherapy - settings.stopPhototherapyDeltaMgDl,
    tcbNeedsTsb,
    bloodTypeDatNeeded,
    followUp,
    before12h,
    prolongedJaundice,
    hearingTestReason,
    hearingTestRecommended: hearingTestReason !== null,
  };
}
